import { scopedPilotageState } from '../services/profitability.mjs';
import FinancialPilotage from '../components/FinancialPilotage';
import InvoiceReport from '../components/InvoiceReport';
import ProspectingReport from "../components/ProspectingReport";
import ActiveContracts from "../components/ActiveContracts";
import AgencyOverview from "../components/AgencyOverview";
import React, { useState } from "react";
import { Panel, Field, Select, Form, Empty } from "../components/ui";
import { todayParis, formatDate, reportingPeriod } from "../utils/calendar.mjs";
import { formatTimestamp } from "../utils/timestamps.mjs";
import {
  userDailyState,
  periodIndicators,
  getDailyGoals,
} from "../services/daily.mjs";
import {
  managementScope,
  managementUsers,
  managementAlerts,
  weeklyGoalReport,
  weeklyLabels,
} from "../services/management.mjs";
import {
  workHoursReport,
  dailyWorkRows,
  formatWorkDuration,
} from "../services/work-hours.mjs";
import {
  buildPilotageReport,
  exportPilotage,
} from "../services/pilotage-export.mjs";
import WorkHoursReport from "../components/WorkHoursReport";
import UsualWeek from "../components/UsualWeek";
export default function Manager({
  state: rawState,
  onClient,
  onTask,
  onCalendar,
  onEditGoals,
  onUsualWeek,
  onWeeklyGoals,
  onNeed,
  onFinancialGoals,
}) {
  const [vision, setVision] = useState("agency");
  const [operational, setOperational] = useState(false);
  const [detailFilters,setDetailFilters] = useState({});
  const state = operational ? scopedPilotageState(rawState,detailFilters) : rawState;
  const [targetUser, setTargetUser] = useState("");
  const [tab, setTab] = useState("agency"),
    [period, setPeriod] = useState("week"),
    [anchor, setAnchor] = useState(todayParis()),
    [owner, setOwner] = useState(""),
    [agency, setAgency] = useState(""),
    [editing, setEditing] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [hoursFilters, setHoursFilters] = useState({
    ...reportingPeriod("week"),
    owner: "",
  });
  const range = reportingPeriod(period, anchor),
    filters = { ...detailFilters, ...range, owner, agency };
  const allUsers = managementUsers(state),
    owners = allUsers.filter(
      ([id]) =>
        (!owner || owner === id) &&
        (!agency ||
          state.clients.some((c) => c.ownerId === id && c.agency === agency)),
    );
  const scope = managementScope(state, agency);
  const alerts = managementAlerts(scope, owners);
  const reports = owners.map(([id, name]) => {
    const own = userDailyState(scope, id);
    return {
      id,
      name,
      own,
      actual: periodIndicators(own, range.from, range.to),
      hours: workHoursReport(state, id, range.from, range.to),
      weekly: weeklyGoalReport(state, id, anchor),
    };
  });
  async function download(format) {
    setBusy(true);
    setError("");
    try {
      await exportPilotage(
        format,
        buildPilotageReport(state, filters, anchor, hoursFilters),
        `pilotage-${range.from}-${range.to}`,
      );
    } catch (e) {
      setError("Export impossible : " + e.message);
    } finally {
      setBusy(false);
    }
  }
  function openAlert(alert, item) {
    if (alert.kind === "tasks" || alert.kind === "invoice") onTask(item);
    else if (alert.kind === "engagement")
      onTask(state.tasks.find((t) => t.id === item.taskId));
    else if (alert.kind === "risk") onClient(item.id);
    else if (alert.kind === "quotes") onClient(item.clientId);
    else {
      setOwner(alert.userId);
      setTab(alert.kind === "hours" ? "hours" : "goals");
    }
  }
  if (!operational) return <FinancialPilotage state={state} onClient={onClient} onTask={onTask} onNeed={onNeed} onSaveGoal={onFinancialGoals} onDetail={f=>{setDetailFilters(f);setOwner(f.owner);setAgency(f.agency);setPeriod(f.period);setAnchor(f.anchor);setVision(f.level==='user'?'user':'agency');setOperational(true);}} />;
  return (
    <div className="managerWorkspace">
      <div className="managerHeading">
        <div>
          <h1>{vision === "agency" ? "Pilotage de l’agence" : "Pilotage par utilisateur"}</h1>
          <button className="textAction" onClick={() => setOperational(false)}>Retour au pilotage commercial</button>
          <p>
            {vision === "agency" ? "Constat de toute l’agence" : "Suivi par utilisateur"} · {formatDate(range.from)} –{" "}
            {formatDate(range.to)}
          </p>
        </div>
        <div className="contactActions">
          <button
            className="primary"
            disabled={busy}
            onClick={() => download("excel")}
          >
            Exporter tout en Excel
          </button>
          <button
            className="secondary"
            disabled={busy}
            onClick={() => download("pdf")}
          >
            Exporter tout en PDF
          </button>
        </div>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <Panel
        title="Période et équipe"
        action={
          <button className="secondary" onClick={onCalendar}>
            Calendrier ouvré
          </button>
        }
      >
        <div className="taskFilters">
          {[
            ["day", "Jour"],
            ["week", "Semaine"],
            ["month", "Mois"],
            ["year", "Année"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={period === key ? "active" : ""}
              aria-pressed={period === key}
              onClick={() => setPeriod(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="managerFilters">
          <Field
            label="Date de référence"
            type="date"
            value={anchor}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
          />
          {vision !== "agency" && <Select
            label="Utilisateur"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
          >
            <option value="">Toute l’équipe</option>
            {allUsers.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </Select>}
          <Select
            label="Agence"
            value={agency}
            onChange={(e) => setAgency(e.target.value)}
          >
            <option value="">Toutes</option>
            {[...new Set(state.clients.map((c) => c.agency))].map((a) => (
              <option key={a}>{a}</option>
            ))}
          </Select>
        </div>
      </Panel>
      {tab !== "prospecting" && <>
      <div className="managerSummary">
        {[
          ["Utilisateurs suivis", owners.length],
          [
            "Alertes prioritaires",
            alerts.filter((a) => a.severity === "high").length,
          ],
          [
            "Appels saisis",
            reports.reduce((n, r) => n + r.actual.callResults, 0),
          ],
          [
            "Besoins détectés",
            reports.reduce((n, r) => n + r.actual.needsDetected, 0),
          ],
        ].map(([label, value]) => (
          <div key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
      <ActiveContracts state={state} filters={filters} onClient={onClient} />
      </>}
      <nav className="taskFilters" aria-label="Rubriques du pilotage">
        {[
          ["agency", "Vue agence"],
          ["team", "Vue équipe"],
          ["prospecting", "Suivi prospection"],
          ["alerts", `Alertes (${alerts.length})`],
          ["goals", "Objectifs et reports"],
          ["hours", "Horaires et heures"],
          ["invoices", "Facturation HT"],
          ["history", "Journal d’activité"],
        ].map(([key, label]) => (
          <button
            key={key}
            className={tab === key ? "active" : ""}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === "agency" && (
        <AgencyOverview
          state={state}
          filters={filters}
          onNeed={onNeed}
          onClient={onClient}
          onTask={onTask}
        />
      )}
      {tab === "invoices" && <InvoiceReport state={state} filters={filters} onClient={onClient} />}
      {tab === "prospecting" && <ProspectingReport state={state} filters={filters} onClient={onClient} onTask={onTask} />}
      {tab === "team" && (
        <Panel title="Performances par utilisateur">
          <div className="tableScroll">
            <table className="taskTable">
              <thead>
                <tr>
                  {[
                    "Utilisateur",
                    "Appels",
                    "Décideurs",
                    "Besoins",
                    "Devis envoyés",
                    "Signatures",
                    "Heures travaillées",
                    "Alertes",
                    "Détail",
                  ].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <b>{r.name}</b>
                    </td>
                    <td>{r.actual.callResults}</td>
                    <td>{r.actual.decisionMakersReached}</td>
                    <td>{r.actual.needsDetected}</td>
                    <td>{r.actual.quotesSent}</td>
                    <td>{r.actual.quotesSigned}</td>
                    <td>{formatWorkDuration(r.hours.actual)}</td>
                    <td>{alerts.filter((a) => a.userId === r.id).length}</td>
                    <td>
                      <button
                        className="secondary"
                        onClick={() => {
                          setOwner(r.id);
                          setTab("history");
                        }}
                      >
                        Voir l’activité
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!reports.length && (
            <Empty>Aucun utilisateur dans ce périmètre.</Empty>
          )}
        </Panel>
      )}
      {tab === "alerts" && (
        <Panel title="Alertes à traiter">
          <p className="hint">
            Situation actuelle, indépendamment de la période de consultation.
          </p>
          {alerts.map((a) => (
            <details className={`managerAlert ${a.severity}`} key={a.id}>
              <summary>
                <b>{a.name}</b> · {a.title} <span>{a.items.length}</span>
              </summary>
              {a.items.map((item, i) => (
                <button
                  className="dailyOpportunity"
                  key={item.id || i}
                  onClick={() => openAlert(a, item)}
                >
                  <span>
                    {item.title ||
                      item.name ||
                      (item.date
                        ? formatDate(item.date) +
                          " · " +
                          formatWorkDuration(item.extra)
                        : weeklyLabels[item.key])}
                    {item.due_date_original || item.due
                      ? " · " + formatDate(item.due_date_original || item.due)
                      : ""}
                  </span>
                  <span>Ouvrir</span>
                </button>
              ))}
            </details>
          ))}
          {!alerts.length && <Empty>Aucune alerte active.</Empty>}
        </Panel>
      )}
      {tab === "goals" && (
        <>
          <Panel title="Objectifs hebdomadaires et reports">
            <p className="hint">
              Semaine du {formatDate(reportingPeriod("week", anchor).from)}. Les
              objectifs fixes se reconduisent chaque semaine. Le reste d’une
              semaine terminée s’ajoute à la suivante si le report est activé.
              Aucun report pendant la semaine en cours.
            </p>
            {reports.map((r) => (
              <div className="managerGoalUser" key={r.id}>
                <div className="managerHeading">
                  <h3>{r.name}</h3>
                  {onWeeklyGoals && (
                    <button
                      className="secondary"
                      onClick={() => setEditing(r.id)}
                    >
                      Fixer les objectifs de la semaine
                    </button>
                  )}
                </div>
                {r.weekly ? (
                  <div className="tableScroll">
                    <table className="taskTable">
                      <thead>
                        <tr>
                          <th>Objectif</th>
                          <th>Base / semaine</th>
                          <th>Report entrant</th>
                          <th>Total attendu</th>
                          <th>Réalisé</th>
                          <th>Reste</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.weekly.factors.map((f) => (
                          <tr key={f.key}>
                            <td>{weeklyLabels[f.key]}</td>
                            <td>{f.base}</td>
                            <td>{f.carry}</td>
                            <td>
                              <b>{f.target}</b>
                            </td>
                            <td>
                              {f.actual}
                              <progress
                                aria-label={`${r.name} ${weeklyLabels[f.key]}`}
                                max={Math.max(1, f.target)}
                                value={Math.min(f.target, f.actual)}
                              />
                            </td>
                            <td>{f.remaining}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <small>
                      Report {r.weekly.plan.carry ? "activé" : "désactivé"} ·
                      Paramétré le {formatTimestamp(r.weekly.plan.updatedAt)}
                    </small>
                  </div>
                ) : (
                  <p className="hint">Aucun objectif hebdomadaire fixé.</p>
                )}
              </div>
            ))}
            {editing && onWeeklyGoals && (
              <Form
                key={editing + anchor}
                onSubmit={(fd) => {
                  onWeeklyGoals(
                    editing,
                    fd.get("week"),
                    Object.fromEntries(
                      Object.keys(weeklyLabels).map((k) => [
                        k,
                        Number(fd.get(k)),
                      ]),
                    ),
                    fd.has("carry"),
                  );
                  setEditing(null);
                }}
              >
                <h3>
                  Objectifs · {allUsers.find(([id]) => id === editing)?.[1]}
                </h3>
                <Field
                  label="Semaine applicable"
                  name="week"
                  type="date"
                  defaultValue={anchor}
                  required
                />
                <div className="formGrid">
                  {Object.entries(weeklyLabels).map(([key, label]) => (
                    <Field
                      key={key}
                      label={label + " par semaine"}
                      name={key}
                      type="number"
                      min="0"
                      max="10000"
                      defaultValue={
                        weeklyGoalReport(state, editing, anchor)?.plan.goals[
                          key
                        ] || 0
                      }
                      required
                    />
                  ))}
                </div>
                <label className="check">
                  <input name="carry" type="checkbox" defaultChecked />
                  Reporter le reste des semaines terminées
                </label>
                <button className="primary">
                  Enregistrer les objectifs hebdomadaires
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setEditing(null)}
                >
                  Annuler
                </button>
              </Form>
            )}
          </Panel>
          <Panel title="Objectifs fixes par utilisateur">
            <div className="tableScroll">
              <table className="taskTable">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    {Object.values(weeklyLabels).map((l) => (
                      <th key={l}>{l} / jour</th>
                    ))}
                    {onEditGoals && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {owners.map(([id, name]) => {
                    const goals = getDailyGoals(state, id);
                    return (
                      <tr key={id}>
                        <td>{name}</td>
                        {Object.keys(weeklyLabels).map((k) => (
                          <td key={k}>{goals[k]}</td>
                        ))}
                        {onEditGoals && (
                          <td>
                            <button
                              className="secondary"
                              onClick={() => onEditGoals(id, name)}
                            >
                              Modifier les objectifs
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
      {tab === "hours" && (
        <>
          {onUsualWeek && (
            <Panel title="Horaires habituels de l’équipe">
              <UsualWeek state={state} owners={owners} onSave={onUsualWeek} />
            </Panel>
          )}
          <WorkHoursReport
            state={state}
            owners={owners}
            onFiltersChange={setHoursFilters}
          >
            {({ range, userId }) => (
              <div className="tableScroll">
                <table className="taskTable">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Utilisateur</th>
                      <th>Horaires</th>
                      <th>Pause</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyWorkRows(
                      state,
                      owners.filter(([id]) => !userId || id === userId),
                      range.from,
                      range.to,
                    ).map((r) => (
                      <tr key={r.userId + r.date}>
                        <td>{formatDate(r.date)}</td>
                        <td>{r.name}</td>
                        <td>
                          {r.hours.source === "empty"
                            ? "Non renseignés"
                            : r.hours.off
                              ? "Non travaillé"
                              : r.hours.start + " – " + r.hours.end}
                        </td>
                        <td>
                          {r.hours.off
                            ? "—"
                            : r.hours.lunchStart
                              ? r.hours.lunchStart + " – " + r.hours.lunchEnd
                              : "—"}
                        </td>
                        <td>
                          {r.hours.source === "confirmed"
                            ? "Changement déclaré"
                            : r.hours.source === "usual"
                              ? "Horaires habituels"
                              : "À renseigner"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </WorkHoursReport>
        </>
      )}
      {tab === "history" && (
        <Panel title="Journal d’activité par utilisateur">
          {reports.map((r) => (
            <div key={r.id}>
              <h3>{r.name}</h3>
              {r.own.events
                .filter((e) => {
                  const date = todayParis(new Date(e.createdAt));
                  return date >= range.from && date <= range.to;
                })
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                .map((e) => (
                  <button
                    className="dailyOpportunity"
                    key={e.id}
                    onClick={() => onClient(e.clientId)}
                  >
                    <div>
                      <b>{e.label}</b>
                      <small>
                        {state.clients.find((c) => c.id === e.clientId)?.name} ·{" "}
                        {formatTimestamp(e.createdAt)}
                      </small>
                    </div>
                  </button>
                ))}
              {!r.own.events.some((e) => {
                const d = todayParis(new Date(e.createdAt));
                return d >= range.from && d <= range.to;
              }) && (
                <p className="hint">
                  Aucune activité enregistrée sur cette période.
                </p>
              )}
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}
